import { AfterViewInit, Component, CUSTOM_ELEMENTS_SCHEMA, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { AuthPolicies } from '../../../auth/auth-policies';
import { RepairModeAdvancedFilterComponent } from '../repair-mode-advanced-filter/repair-mode-advanced-filter.component';
import { TagWithOverflowComponent } from '../../../shared-components/tag-with-overflow/tag-with-overflow.component';
import { ToastrService } from '../../../services/toastr-service';
import { RepairModeService } from '../../../services/repair-mode.service';
import { RepairModeFilterForm } from '../../../form-model/repair-mode-filter.form.model';
import { RepairModeSearchTextForm } from '../../../form-model/repair-mode-search-text.form.model';
import { environment } from '../../../../environments/environment';
import { DownloadFilterCriteria, toFilter, toEqualsFilter } from '../../../models/download-filter.model';
import { downloadBlob, handleDownloadError, generateDateFilename } from '../../../helper/download-helpers';
import { getFilterValue as getSharedFilterValue, removeFilter as removeSharedFilter } from '../../../helper/filter-helpers';
import '@maersk-global/mds-components-core/mc-tag';
import '@maersk-global/mds-components-core/mc-modal';
import '@maersk-global/mds-components-core/mc-tooltip';
import '@maersk-global/mds-components-core/mc-button';

type FilterValue = string | string[];

@Component({
  selector: 'app-repair-mode-filters',
  imports: [CommonModule, ReactiveFormsModule, RouterModule, RepairModeAdvancedFilterComponent, TagWithOverflowComponent],
  templateUrl: './repair-mode-filters.component.html',
  styleUrl: './repair-mode-filters.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class RepairModeFiltersComponent implements AfterViewInit {
  showAdvanceFilters = false;
  isDownload = false;

  canAddNewRepairMode = this.authPolicies.canAddRepairMode && environment.Features.EnableRepairMode;

  @Input({ required: true }) filterFormGroup!: FormGroup<{
    repairModeFilterForm: FormGroup<RepairModeFilterForm>;
    repairModeSearchTextForm: FormGroup<RepairModeSearchTextForm>;
  }>;

  taggedFilters: { key: string; value: FilterValue }[] = [];

  @ViewChild(RepairModeAdvancedFilterComponent) advanceFilterComponent!: RepairModeAdvancedFilterComponent;

  constructor(
    public readonly authPolicies: AuthPolicies,
    private readonly formBuilder: FormBuilder,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly repairModeService: RepairModeService,
  ) {}

  ngAfterViewInit(): void {
    this.ensureFlagControlsExist();
    this.subscribeToFormChanges();
    this.setFilterFormValue();
  }

  private ensureFlagControlsExist(): void {
    const filterForm = this.filterFormGroup.controls.repairModeFilterForm;
    const searchForm = this.filterFormGroup.controls.repairModeSearchTextForm;

    if (!filterForm.get('applyRepairModeFilter')) {
      filterForm.addControl('applyRepairModeFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!searchForm.get('appliedRepairModeSearchTextFilter')) {
      searchForm.addControl('appliedRepairModeSearchTextFilter', this.formBuilder.control<boolean | null>(null));
    }
    if (!filterForm.get('repairModeCodeList')) {
      filterForm.addControl('repairModeCodeList', this.formBuilder.control<string[] | null>(null));
    }
    if (!filterForm.get('activeStatusesList')) {
      filterForm.addControl('activeStatusesList', this.formBuilder.control<string | null>(null));
    }
  }

  private setFilterFormValue(): void {
    const filterValue = this.repairModeService.getRepairModeFilterFormValue();
    if (filterValue) {
      this.filterFormGroup.controls.repairModeFilterForm.patchValue(filterValue, { emitEvent: false });
      this.updateSelectedFiltersSection();
      setTimeout(() => {
        this.filterFormGroup.controls.repairModeFilterForm.get('applyRepairModeFilter')?.setValue(true);
      });
    }
  }

  private subscribeToFormChanges(): void {
    this.filterFormGroup.controls.repairModeFilterForm.get('applyRepairModeFilter')!.valueChanges.subscribe((value) => {
      if (value) {
        this.filterFormGroup.controls.repairModeFilterForm.get('applyRepairModeFilter')!.setValue(null, { emitEvent: false });
        this.showAdvanceFilters = false;
        this.updateSelectedFiltersSection();
        this.repairModeService.setRepairModeFilterFormValue(
          this.filterFormGroup.controls.repairModeFilterForm.getRawValue(),
        );
      }
    });
    this.filterFormGroup.controls.repairModeSearchTextForm.get('appliedRepairModeSearchTextFilter')!.valueChanges.subscribe(() => {
      this.updateSelectedFiltersSection();
    });
  }

  showAdvanceFiltersModal(): void {
    this.showAdvanceFilters = true;
  }

  applyAdvancedFilters(): void {
    this.filterFormGroup.controls.repairModeFilterForm.get('applyRepairModeFilter')?.setValue(true);
  }

  clearAdvancedFilters(): void {
    const filterForm = this.filterFormGroup.controls.repairModeFilterForm;
    filterForm.reset({ repairModeCodeList: null, activeStatusesList: null });
    filterForm.get('applyRepairModeFilter')?.setValue(true);
  }

  closeAdvanceFiltersModal(): void {
    if (this.showAdvanceFilters) {
      this.showAdvanceFilters = false;
      const lastApplied = this.repairModeService.getRepairModeFilterFormValue();
      this.filterFormGroup.controls.repairModeFilterForm.patchValue(
        {
          repairModeCodeList: lastApplied?.repairModeCodeList ?? null,
          activeStatusesList: lastApplied?.activeStatusesList ?? null,
        },
        { emitEvent: false },
      );
    }
    this.updateSelectedFiltersSection();
  }

  clearAllSelectedFilters(): void {
    this.filterFormGroup.reset();
    this.filterFormGroup.controls.repairModeFilterForm.get('applyRepairModeFilter')?.setValue(true);
    this.repairModeService.setRepairModeFilterFormValue(null);
    this.updateSelectedFiltersSection();
  }

  unselectRepairModeCode(): (_: string) => void {
    return (code: string) => {
      const control = this.filterFormGroup.controls.repairModeFilterForm.get('repairModeCodeList');
      const currentValues = (control?.value as string[]) ?? [];
      const updatedValue = currentValues.filter((c) => c !== code);
      control?.setValue(updatedValue.length ? updatedValue : null);
      this.filterFormGroup.controls.repairModeFilterForm.get('applyRepairModeFilter')?.setValue(true);
      this.updateSelectedFiltersSection();
    };
  }

  unselectActive(): void {
    this.filterFormGroup.controls.repairModeFilterForm.get('activeStatusesList')?.setValue(null);
    this.filterFormGroup.controls.repairModeFilterForm.get('applyRepairModeFilter')?.setValue(true);
    this.updateSelectedFiltersSection();
  }

  updateSelectedFiltersSection(): void {
    const form = this.filterFormGroup.controls.repairModeFilterForm;
    this.markAsTagged('Repair Mode', form.get('repairModeCodeList')?.value ?? null);
    this.markAsTagged('Active', form.get('activeStatusesList')?.value ?? null);
  }

  markAsTagged(key: string, newValue: FilterValue | null): void {
    const currentValue = this.getFilterValue(key);
    if (currentValue !== newValue) {
      this.removeFilter(key);
      if (newValue !== null && newValue !== '' && (!Array.isArray(newValue) || newValue.length > 0)) {
        this.taggedFilters.unshift({ key, value: newValue });
      }
    }
  }

  removeFilter(key: string): void {
    removeSharedFilter(this.taggedFilters, key);
  }

  getFilterValue(key: string): FilterValue | null {
    return getSharedFilterValue(this.taggedFilters, key) as FilterValue | null;
  }

  getTaggedRepairModeCodes(): string[] {
    const filter = this.taggedFilters.find((f) => f.key === 'Repair Mode');
    return filter && Array.isArray(filter.value) ? filter.value : [];
  }

  getTaggedActive(): string | null {
    const filter = this.taggedFilters.find((f) => f.key === 'Active');
    return filter && !Array.isArray(filter.value) ? filter.value : null;
  }

  isArrayType(value: FilterValue): boolean {
    return Array.isArray(value);
  }

  onAddNewRepairModeClicked(): void {
    this.router.navigate(['/master-data/repair-mode/add-new']);
  }

  onDownload(): void {
    this.isDownload = true;
    const filters = this.buildDownloadFilters();
    this.repairModeService.downloadToExcel({ columns: [], filters }).subscribe({
      next: (blob) => {
        downloadBlob(blob, generateDateFilename('repair-mode'));
        this.isDownload = false;
      },
      error: (err) => {
        handleDownloadError(err, this.toastrService);
        this.isDownload = false;
      },
    });
  }

  private buildDownloadFilters(): DownloadFilterCriteria[] {
    const filters: DownloadFilterCriteria[] = [];
    const form = this.filterFormGroup.controls.repairModeFilterForm;

    const codes = form.get('repairModeCodeList')?.value;
    if (codes && codes.length > 0) {
      const codeParts = codes.map((c) => c.split(' - ')[0]);
      filters.push(toFilter('mode', codeParts));
    }

    const activeVal = form.get('activeStatusesList')?.value;
    if (activeVal !== null && activeVal !== undefined && activeVal !== '') {
      filters.push(toEqualsFilter('is_active', activeVal === 'true' ? '1' : '0'));
    }

    return filters;
  }
}
